import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Modal } from 'react-bootstrap';
import BlueprintWithMarkers, { BlueprintHighlight, NumberedMarker } from './BlueprintWithMarkers';
import { BlueprintLocation, Building, PhotoModel } from '../types/protocols';
import { BaseComponentImpl, RoomImpl } from '../types/implementations';
import { RelocatableMarker, proposedLocation, withBlueprintLocation } from '../Helpers/blueprintRelocation';

export interface RelocationDrawing {
    image: PhotoModel;
    imageKey: string;
}

interface BlueprintRelocationWizardProps {
    building: Building;
    /** The drawing being replaced; every item has a position on it. */
    source: RelocationDrawing;
    /** The uploaded drawing, not yet current. */
    target: RelocationDrawing;
    items: RelocatableMarker[];
    /** Writes everything; the wizard stays open with the error if it throws. */
    onCommit: (placements: Map<string, BlueprintLocation>) => Promise<void>;
    /** Nothing was written; the page tombstones the upload and closes the wizard. */
    onAbort: () => Promise<void> | void;
}

type Dialog = 'abort' | 'finish' | null;

const ABORT_TEXT = 'Ritningen byts inte ut. Nuvarande ritning och alla dess markörer behålls. Placeringar du gjort i det här steget försvinner.';

// Keys that already mean something to the focused element (typing, or Enter
// activating a focused button) are left alone so nothing fires twice.
const isTypingTarget = (target: EventTarget | null): boolean => {
    const element = target as HTMLElement | null;
    if (!element) return false;
    const tag = element.tagName;
    return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || tag === 'BUTTON' || element.isContentEditable;
};

/**
 * Moves markers from a replaced drawing to its replacement, one by one. All
 * state is in memory: Slutför hands the placements to the page for a single
 * commit, Avbryt hands nothing over. See the blueprint marker relocation plan.
 */
const BlueprintRelocationWizard: React.FC<BlueprintRelocationWizardProps> = ({
    building,
    source,
    target,
    items,
    onCommit,
    onAbort,
}) => {
    const [index, setIndex] = useState(0);
    const [placements, setPlacements] = useState<Map<string, BlueprintLocation>>(() => new Map());
    const [skipped, setSkipped] = useState<Set<string>>(() => new Set());
    const [dialog, setDialog] = useState<Dialog>(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const current = items[index];
    const isLast = index >= items.length - 1;
    const currentLocation = useMemo((): BlueprintLocation | undefined => {
        if (!current) return undefined;
        return placements.get(current.id) ?? proposedLocation(current.sourceLocation, { key: target.imageKey, width: target.image.width, height: target.image.height });
    }, [current, placements, target]);

    // Items neither placed nor explicitly skipped; the Slutför dialog counts these.
    const countUnreached = useCallback(
        (p: Map<string, BlueprintLocation>, s: Set<string>) => items.filter(item => !p.has(item.id) && !s.has(item.id)).length,
        [items]
    );
    const unreachedCount = useMemo(() => countUnreached(placements, skipped), [countUnreached, placements, skipped]);

    const finish = useCallback(async (toCommit: Map<string, BlueprintLocation>) => {
        setBusy(true);
        setError(null);
        try {
            await onCommit(toCommit);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Ritningen kunde inte sparas');
            setBusy(false);
        }
    }, [onCommit]);

    // Takes the next state explicitly: after the last item's Nästa/Hoppa över the
    // React state has not been applied yet when the decision is made.
    const requestFinish = useCallback((p: Map<string, BlueprintLocation>, s: Set<string>) => {
        if (countUnreached(p, s) > 0) {
            setDialog('finish');
            return;
        }
        finish(p);
    }, [countUnreached, finish]);

    const advance = useCallback((p: Map<string, BlueprintLocation>, s: Set<string>) => {
        if (isLast) {
            requestFinish(p, s);
        } else {
            setIndex(i => i + 1);
        }
    }, [isLast, requestFinish]);

    const handleNext = useCallback(() => {
        if (!current || !currentLocation) return;
        const nextPlacements = new Map(placements).set(current.id, currentLocation);
        const nextSkipped = new Set(skipped);
        nextSkipped.delete(current.id);
        setPlacements(nextPlacements);
        setSkipped(nextSkipped);
        advance(nextPlacements, nextSkipped);
    }, [current, currentLocation, placements, skipped, advance]);

    const handleSkip = useCallback(() => {
        if (!current) return;
        const nextPlacements = new Map(placements);
        nextPlacements.delete(current.id);
        const nextSkipped = new Set(skipped).add(current.id);
        setPlacements(nextPlacements);
        setSkipped(nextSkipped);
        advance(nextPlacements, nextSkipped);
    }, [current, placements, skipped, advance]);

    const handleBack = useCallback(() => {
        setIndex(i => Math.max(0, i - 1));
    }, []);

    const handlePlace = useCallback((location: BlueprintLocation) => {
        if (!current) return;
        setPlacements(prev => new Map(prev).set(current.id, location));
    }, [current]);

    const handleAbort = useCallback(async () => {
        setBusy(true);
        setError(null);
        try {
            await onAbort();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Ritningen kunde inte tas bort');
            setBusy(false);
            setDialog(null);
        }
    }, [onAbort]);

    // Enter = Nästa, Esc = Avbryt dialog. Only while no dialog is open.
    useEffect(() => {
        if (dialog || busy) return;
        const onKeyDown = (e: KeyboardEvent) => {
            if (isTypingTarget(e.target)) return;
            if (e.key === 'Enter') {
                e.preventDefault();
                handleNext();
            } else if (e.key === 'Escape') {
                e.preventDefault();
                setDialog('abort');
            }
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [dialog, busy, handleNext]);

    // Source pane: every item on the old drawing, the current one enlarged.
    const sourceRooms = useMemo(() => items.map(item => item.room).filter((room): room is RoomImpl => !!room), [items]);
    const sourceComponents = useMemo(() => items.map(item => item.component).filter((c): c is BaseComponentImpl => !!c), [items]);
    const sourceSpots = useMemo(() => items.map(item => item.numbered).filter((n): n is NumberedMarker => !!n), [items]);

    // Target pane: items already placed, plus the current one at its proposed or clicked position.
    const targetEntities = useMemo(() => {
        const rooms: RoomImpl[] = [];
        const components: BaseComponentImpl[] = [];
        const spots: NumberedMarker[] = [];
        for (const item of items) {
            const location = item.id === current?.id ? currentLocation : placements.get(item.id);
            if (!location) continue;
            if (item.room) rooms.push(withBlueprintLocation(item.room, target.imageKey, location));
            else if (item.component) components.push(withBlueprintLocation(item.component, target.imageKey, location));
            else if (item.numbered) spots.push({ ...item.numbered, blueprintLocations: { [target.imageKey]: location } });
        }
        return { rooms, components, spots };
    }, [items, current, currentLocation, placements, target.imageKey]);

    const highlight = useMemo((): BlueprintHighlight | undefined => {
        if (!current) return undefined;
        const kind = current.room ? 'room' : current.component ? 'utility' : 'spot';
        return { kind, key: current.entityKey };
    }, [current]);

    const openAbortDialog = () => { if (!busy) setDialog('abort'); };

    return (
        <>
            <Modal show size="xl" keyboard={false} onHide={openAbortDialog} centered animation={false}>
                <Modal.Header closeButton>
                    <Modal.Title>Flytta markörer till den nya ritningen</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p style={{ marginBottom: '12px' }}>
                        <strong>{Math.min(index + 1, items.length)} av {items.length}</strong>
                        {current && <> · {current.displayName}</>}
                        <span style={{ color: '#666' }}> — klicka på den nya ritningen där markören ska ligga, eller bekräfta det föreslagna läget med Nästa.</span>
                    </p>
                    {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
                    <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                        <div style={{ flex: '1 1 380px', minWidth: 0 }}>
                            <BlueprintWithMarkers
                                blueprint={source.image}
                                currentBlueprintKey={source.imageKey}
                                building={building}
                                rooms={sourceRooms}
                                utilities={sourceComponents}
                                spots={sourceSpots}
                                highlight={highlight}
                                title="Nuvarande ritning"
                            />
                        </div>
                        <div style={{ flex: '1 1 380px', minWidth: 0 }}>
                            <BlueprintWithMarkers
                                blueprint={target.image}
                                currentBlueprintKey={target.imageKey}
                                building={building}
                                rooms={targetEntities.rooms}
                                utilities={targetEntities.components}
                                spots={targetEntities.spots}
                                highlight={highlight}
                                placementMode
                                onBlueprintClick={handlePlace}
                                title="Ny ritning"
                            />
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-danger" onClick={openAbortDialog} disabled={busy} style={{ marginRight: 'auto' }}>
                        Avbryt
                    </Button>
                    <Button variant="outline-secondary" onClick={handleBack} disabled={busy || index === 0}>Tillbaka</Button>
                    <Button variant="outline-secondary" onClick={handleSkip} disabled={busy || !current}>Hoppa över</Button>
                    <Button variant="primary" onClick={handleNext} disabled={busy || !current}>Nästa</Button>
                    <Button variant="success" onClick={() => requestFinish(placements, skipped)} disabled={busy}>
                        {busy ? 'Sparar...' : 'Slutför'}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={dialog === 'abort'} onHide={() => setDialog(null)} centered animation={false}>
                <Modal.Header closeButton>
                    <Modal.Title>Avbryt bytet av ritning?</Modal.Title>
                </Modal.Header>
                <Modal.Body>{ABORT_TEXT}</Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setDialog(null)} disabled={busy}>Fortsätt placera</Button>
                    <Button variant="danger" onClick={handleAbort} disabled={busy}>Avbryt bytet</Button>
                </Modal.Footer>
            </Modal>

            <Modal show={dialog === 'finish'} onHide={() => setDialog(null)} centered animation={false}>
                <Modal.Header closeButton>
                    <Modal.Title>Alla markörer är inte placerade</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {unreachedCount === 1
                        ? '1 markör har inte placerats och kommer att saknas på den nya ritningen.'
                        : `${unreachedCount} markörer har inte placerats och kommer att saknas på den nya ritningen.`}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setDialog(null)} disabled={busy}>Fortsätt placera</Button>
                    <Button variant="warning" onClick={() => { setDialog(null); finish(placements); }} disabled={busy}>Slutför ändå</Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default BlueprintRelocationWizard;
